'use client';

import { DOMNode } from 'html-react-parser';
import { useEffect, useRef, useState } from 'react';

interface FlourishEmbedProps {
    dataSrc: string;
    className?: string;
}

function FlourishEmbed({ dataSrc, className }: FlourishEmbedProps) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [height, setHeight] = useState(400);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            // Verify the message is from Flourish
            if (!event.origin.includes('flourish') && !event.origin.includes('flo.uri.sh')) return;

            // Check if the message is for this specific iframe
            if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;

            // Parse message data - Flourish may send as string or object
            let data = event.data;
            if (typeof data === 'string') {
                try {
                    data = JSON.parse(data);
                } catch {
                    return;
                }
            }

            // Flourish sends height in various formats
            const newHeight = data?.height || data?.embed_height || data?.data?.height;
            if (typeof newHeight === 'number' && newHeight > 0) {
                setHeight(newHeight);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    return (
        <div className={className}>
            <iframe
                ref={iframeRef}
                title="Interactive or visual content"
                sandbox="allow-same-origin allow-forms allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation"
                style={{ width: '100%', height: `${height}px`, border: 'none' }}
                src={`https://flo.uri.sh/${dataSrc}/embed?auto=1`}
            />
        </div>
    );
}

export function processFlourishEmbed(domNode: DOMNode): React.ReactNode | undefined {
    if (domNode.type === 'tag' && 'attribs' in domNode) {
        const className = domNode.attribs?.class || '';
        const dataSrc = domNode.attribs?.['data-src'];

        if (className.includes('flourish-embed') && dataSrc) {
            return (
                <FlourishEmbed
                    dataSrc={dataSrc}
                    className={className}
                />
            );
        }
    }
    return undefined;
}

export default FlourishEmbed;